import React, { useState, useEffect } from 'react';
import { Check, Shield, Info, X } from 'lucide-react';

// Color constants (matching LetterCreation.js)
const colors = {
  primaryGreen: '#556B2F',
  secondaryGreen: '#6B8E23',
  highlightOrange: '#E65100',
  textDark: '#333333',
  textMedium: '#666666',
  textMuted: '#999999',
};

/**
 * StewardSelection Component
 *
 * Allows users to optionally select a trusted family member (steward)
 * who can deliver the letter if the sender becomes unavailable.
 *
 * Props:
 * - familyMembers: Array of family members to choose from
 * - selectedSteward: Currently selected steward (Person object or null)
 * - onStewardChange: Callback when steward selection changes
 * - recipientId: ID of the letter recipient (to exclude from steward options)
 * - disabled: Whether the selection is disabled
 * - title / roleLabel / noneLabel: wording, so the same component serves the Backup steward
 * - excludeId: person already chosen for the other role (can't hold both)
 * - compact: hide the info panel and tip (for the second instance on a page)
 */
const StewardSelection = ({
  familyMembers = [],
  selectedSteward = null,
  onStewardChange,
  recipientId = null,
  disabled = false,
  title = 'Choose a Trusted Steward',
  roleLabel = 'Trusted Steward',
  noneLabel = 'No Steward',
  excludeId = null,
  compact = false,
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter out the recipient and minors from steward options
  // Only adults (18+) can serve as stewards
  const availableStewards = familyMembers.filter(
    member => member.person_id !== recipientId && member.id !== recipientId && !member.is_minor
      && (!excludeId || String(member.person_id || member.id) !== String(excludeId))
  );

  // Get avatar emoji based on relationship/gender
  const getAvatarEmoji = (member) => {
    const gender = member.gender || member.person?.gender;
    if (gender === 'M') return '👨';
    if (gender === 'F') return '👩';

    const role = (member.role || '').toLowerCase();
    const typeMap = {
      'son': '👦', 'daughter': '👧', 'wife': '👩', 'husband': '👨',
      'mother': '👩', 'father': '👨', 'brother': '👦', 'sister': '👧',
      'grandmother': '👵', 'grandfather': '👴',
    };
    return typeMap[role] || '👤';
  };

  const handleSelectSteward = (member) => {
    if (disabled) return;

    const memberId = String(member.person_id || member.id);
    const currentId = String(selectedSteward?.person_id || selectedSteward?.id || '');

    // Toggle selection if clicking the same steward
    if (currentId === memberId) {
      onStewardChange(null);
    } else {
      onStewardChange(member);
    }
    // Auto-collapse after selection
    setIsExpanded(false);
  };

  const handleSkip = () => {
    onStewardChange(null);
    setIsExpanded(false);
  };

  return (
    <div className="mb-6">
      {/* Header with Info Toggle */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield size={18} style={{ color: colors.primaryGreen }} />
          <span className="font-medium" style={{ color: colors.textDark }}>
            {title}
          </span>
          <span className="text-sm" style={{ color: colors.textMuted }}>(Optional)</span>
        </div>
        {!compact && (
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <Info size={18} style={{ color: colors.textMuted }} />
          </button>
        )}
      </div>

      {/* Info Panel */}
      {!compact && showInfo && (
        <div
          className="rounded-xl p-4 mb-4 relative"
          style={{ backgroundColor: '#F5F5F0' }}
        >
          <button
            onClick={() => setShowInfo(false)}
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200"
          >
            <X size={16} style={{ color: colors.textMuted }} />
          </button>
          <h4 className="font-medium mb-2" style={{ color: colors.textDark }}>
            What is a Steward?
          </h4>
          <p className="text-sm mb-3" style={{ color: colors.textMedium }}>
            A steward is someone you trust to ensure your letter gets delivered
            if you're no longer able to do so yourself.
          </p>
          <ul className="text-sm space-y-2" style={{ color: colors.textMedium }}>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Your steward <strong>cannot read</strong> your letter's contents</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>They can only confirm milestone completion to trigger delivery</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>They're notified only if you become unavailable</span>
            </li>
          </ul>
        </div>
      )}

      {/* Current Selection Display */}
      {selectedSteward && !isExpanded && (
        <div
          className="bg-white rounded-xl p-4 shadow-sm mb-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
          onClick={() => setIsExpanded(true)}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#F0F4E8' }}
            >
              <span className="text-xl">{getAvatarEmoji(selectedSteward)}</span>
            </div>
            <div>
              <div className="font-medium" style={{ color: colors.textDark }}>
                {selectedSteward.first_name || selectedSteward.person?.first_name}
              </div>
              <div className="text-xs" style={{ color: colors.textMuted }}>
                {roleLabel}
              </div>
            </div>
          </div>
          <button
            className="text-sm px-3 py-1 rounded-full"
            style={{ backgroundColor: '#F0F4E8', color: colors.primaryGreen }}
          >
            Change
          </button>
        </div>
      )}

      {/* Selection Grid - Show when no selection or expanded */}
      {(!selectedSteward || isExpanded) && (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            {availableStewards.map((member) => {
              const personId = member.person_id || member.id;
              const currentStewardId = String(selectedSteward?.person_id || selectedSteward?.id || '');
              const isSelected = currentStewardId === String(personId);
              const name = member.first_name || member.person?.first_name || 'Unknown';
              const role = member.role || 'Family';

              return (
                <div
                  key={personId}
                  className={`relative rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
                    isSelected ? 'ring-2 ring-offset-2' : ''
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{
                    backgroundColor: isSelected ? '#F0F4E8' : '#FFFFFF',
                    borderWidth: isSelected ? 2 : 1.5,
                    borderStyle: 'solid',
                    borderColor: isSelected ? colors.secondaryGreen : '#E0E0E0',
                    minWidth: '100px',
                  }}
                  onClick={() => handleSelectSteward(member)}
                >
                  {isSelected && (
                    <div
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: colors.secondaryGreen }}
                    >
                      <Check size={14} className="text-white" />
                    </div>
                  )}
                  <div className="text-center">
                    <div className="text-2xl mb-1">{getAvatarEmoji(member)}</div>
                    <div className="text-sm font-medium" style={{ color: colors.primaryGreen }}>
                      {name}
                    </div>
                    <div className="text-xs" style={{ color: colors.textMuted }}>
                      ({role})
                    </div>
                  </div>
                </div>
              );
            })}

            {/* No Steward Option */}
            <div
              className={`rounded-xl p-4 cursor-pointer transition-all hover:shadow-md border ${
                !selectedSteward ? 'ring-2 ring-offset-2' : ''
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{
                backgroundColor: !selectedSteward ? '#FFF8E1' : '#FFFFFF',
                borderColor: !selectedSteward ? '#FFB74D' : '#E0E0E0',
                borderStyle: !selectedSteward ? 'solid' : 'dashed',
                minWidth: '100px',
              }}
              onClick={handleSkip}
            >
              {!selectedSteward && (
                <div
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#FFB74D' }}
                >
                  <Check size={14} className="text-white" />
                </div>
              )}
              <div className="text-center">
                <div className="text-2xl mb-1">🙅</div>
                <div className="text-sm font-medium" style={{ color: colors.textMedium }}>
                  {noneLabel}
                </div>
                <div className="text-xs" style={{ color: colors.textMuted }}>
                  (I'll manage it)
                </div>
              </div>
            </div>
          </div>

          {isExpanded && (
            <button
              onClick={() => setIsExpanded(false)}
              className="text-sm underline"
              style={{ color: colors.textMuted }}
            >
              Done selecting
            </button>
          )}
        </>
      )}

      {/* Tip */}
      {!compact && !showInfo && (
        <div className="rounded-xl p-3" style={{ backgroundColor: '#F5F5F0' }}>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            <Shield size={14} className="inline mr-1" style={{ color: colors.secondaryGreen }} />
            A steward ensures your letter reaches {' '}
            its recipient, even if you're not around.
          </p>
        </div>
      )}
    </div>
  );
};

export default StewardSelection;
